// crewbit-admin/src/components/money/MarkBadDebtModal.tsx
//
// §Money.StubActionSweepCompletion Phase 1. Centered confirm modal for the
// mark_invoice_bad_debt RPC. Destructive (XOctagon icon, redWash background) +
// optional reason textarea. INSUFFICIENT_AGE_FOR_BAD_DEBT surfaces as in-modal
// banner with days-remaining meta — modal stays open so the operator gets
// visible feedback per `feedback_anti_silent_operations.md`.

import { useState } from 'react';
import { XOctagon } from 'lucide-react';
import { palette, spacing, typography, transition } from '../../styles/tokens';
import { markBadDebt } from '../../lib/invoiceMutations';

interface Props {
  isOpen: boolean;
  invoiceId: string;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

export function MarkBadDebtModal({ isOpen, invoiceId, onClose, onResult }: Props) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  if (!isOpen) return null;

  const reset = () => {
    setReason('');
    setInlineError(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const onConfirm = async () => {
    setLoading(true);
    setInlineError(null);
    const trimmed = reason.trim();
    const result = await markBadDebt(invoiceId, trimmed || 'Operator-asserted bad debt');
    setLoading(false);
    if (result.success) {
      onResult(true, result.data.already_written_off ? 'Already written off.' : 'Invoice written off.');
      reset();
      onClose();
    } else if (result.error.code === 'INSUFFICIENT_AGE_FOR_BAD_DEBT' && result.error.meta) {
      const ageDays = Number(result.error.meta.age_days ?? 0);
      const requiredDays = Number(result.error.meta.required_days ?? 90);
      const remaining = Math.max(0, requiredDays - ageDays);
      setInlineError(
        `Bad-debt write-off requires the invoice to be ≥${requiredDays} days old. ` +
        `Current age: ${ageDays} days. Available in ${remaining} days.`,
      );
    } else {
      onResult(false, result.error.message ?? 'Failed to write off invoice.');
    }
  };

  return (
    <>
      <div onClick={handleClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 420, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.redWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <XOctagon size={22} color={palette.red} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Write off as bad debt?
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          Bad-debt write-off marks this invoice as uncollectable for tax purposes.
          Only invoices ≥90 days old can be written off.
        </div>

        <label style={{ display: 'block', fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
          Reason (optional)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 500))}
          placeholder="e.g. Client unreachable since 2026-02"
          rows={3}
          style={{
            width: '100%', padding: spacing.sm, borderRadius: 8,
            border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
            fontSize: typography.size.caption, color: palette.g800,
            resize: 'vertical', boxSizing: 'border-box',
          }}
        />

        {inlineError && (
          <div style={{
            marginTop: spacing.sm,
            padding: spacing.sm,
            background: palette.amberWash,
            border: `1px solid ${palette.amber}`,
            borderRadius: 8,
            fontSize: typography.size.caption,
            color: palette.g800,
          }}>
            {inlineError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={handleClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: loading ? palette.g300 : palette.red, color: palette.white,
            fontSize: 13.5, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? 'Writing off…' : 'Write off'}</button>
        </div>
      </div>
    </>
  );
}
